import React from 'react';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import { useTeamsProfile } from '../../hooks/use-hydra';
import styled, { css, useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { H3, P, PExtraSmall, PSmall } from '../../components/elements/Text';
import { MultiLineInput } from '../../components/elements/Input';
import { ColumnQuestionWrapper } from '../../components/Onboarding/Typeform/common';
import Button from '../../components/elements/Button';
import autoAnimate from '@formkit/auto-animate';
import { useMutation } from 'react-query';
import useAuth from '../../hooks/use-auth';
import { postCohortOnboarding, TeamsProfilePayload } from '../../api/teams';
import dayjs from 'dayjs';
import { AppliedProjectStat, StaticLearningPlanUnit } from '../../hydra';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AxiosError } from 'axios';
import {
  LearningPlanWrapper,
  mapLearningPlanUnit,
} from '../../components/LearningPlan/StaticLearningPlan';
import LearningPlanUnitCard from '../../components/LearningPlan/LearningPlanUnit';
import { useRive } from '@rive-app/react-canvas';
// @ts-expect-error - .riv files are not supported by typescript
import loaderAnim from '../../assets/rive-animations/loader.riv';
import TimezoneTimeQuestion from './TimezoneTimeQuestion';
import { ProgressWidget } from './TeamsProfileOnboarding';
import { useTeamsProjectsDashboard } from '../../hooks/use-hydra/teams';
import { ROUTES } from '../../routing';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export type NotificationTimeAnswer = { timezone: string; time: string };
type SingleSelectAnswer = string;
type MultiSelectAnswer = string[];
type InputAnswer = string;
type SkillRatingAnswer = { [topic: string]: number };

type BaseQuestion = {
  id: string;
  text: string;
  subtext?: string;
};

type TimezoneTimeQuestion = BaseQuestion & {
  type: 'timezone-time';
};

type MultiSelectQuestion = BaseQuestion & {
  type: 'multi-select';
  options: Array<{ value: string; label: string }>;
};

type SingleSelectQuestion = BaseQuestion & {
  type: 'single-select';
  options: Array<{ value: string; label: string }>;
};

type LearningPlanInputQuestion = BaseQuestion & {
  type: 'learning-plan-input';
  learningPlan: StaticLearningPlanUnit[] | null;
};

type InputQuestion = BaseQuestion & {
  type: 'input';
};

type SkillRatingQuestion = BaseQuestion & {
  type: 'skill-rating';
  topics: string[];
};

type LoaderQuestion = BaseQuestion & {
  type: 'loader';
};

type Question =
  | TimezoneTimeQuestion
  | SingleSelectQuestion
  | MultiSelectQuestion
  | LearningPlanInputQuestion
  | InputQuestion
  | SkillRatingQuestion
  | LoaderQuestion;

type Answers = {
  'ask-notification-time'?: NotificationTimeAnswer;
  'ask-kickoff-time'?: SingleSelectAnswer;
  'ask-office-hours-time'?: MultiSelectAnswer;
  'ask-learning-plan-feedback'?: InputAnswer;
  'ask-skill-rating'?: SkillRatingAnswer;
  'ask-common-tasks'?: InputAnswer;
  [key: string]:
    | NotificationTimeAnswer
    | MultiSelectAnswer
    | SingleSelectAnswer
    | InputAnswer
    | SkillRatingAnswer
    | undefined;
};

export const AnimationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  height: 100%;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  min-height: 100vh;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
`;

export const NavigationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 20px;
  align-items: center;
`;

export const NavButton = styled(Button)`
  /* border: none !important; */
`;

export default function TeamsProfileOnboarding() {
  const {
    value: teamsProfile,
    retry: refreshTeamsData,
    loading: isLoadingTeams,
    error,
  } = useTeamsProfile();
  const { value: projects } = useTeamsProjectsDashboard();
  const { getTokenSilently } = useAuth();
  const history = useHistory();
  const openCohortInvite = teamsProfile?.openCohortInvites?.[0];

  const kickOffTimes = React.useMemo(
    () => openCohortInvite?.cohort?.kickOffTimes ?? [],
    [openCohortInvite]
  );

  const officeHoursTimes = React.useMemo(
    () => openCohortInvite?.cohort?.officeHourTimes ?? [],
    [openCohortInvite]
  );
  const shouldShowKickOff =
    teamsProfile?.company?.settings?.KICK_OFF && kickOffTimes.length > 1;
  const shouldAnswerKickOff =
    teamsProfile?.company?.settings?.KICK_OFF && kickOffTimes.length > 0;

  const learningPlanTopics = parseLearningPlanTopics({
    learningPlan: teamsProfile?.populatedLearningPlan ?? [],
    modules: teamsProfile?.modules ?? [],
    projects: projects ?? [],
  });

  // Function to get skill knowledge options for each topic
  const getSkillKnowledgeOptions = (topic: string) => {
    const normalizedTopic = topic.toLowerCase();

    if (
      normalizedTopic.includes('genai') ||
      normalizedTopic.includes('ai') ||
      normalizedTopic.includes('llm')
    ) {
      return [
        "I've used ChatGPT or similar AI tools before",
        "I'm familiar with basic prompting techniques",
        'I understand how to break down complex tasks for AI',
        'I know how to evaluate and improve AI outputs',
        'I can create sophisticated prompting chains and workflows',
        'None of the above',
      ];
    }

    if (normalizedTopic.includes('python')) {
      return [
        "I've written Python code before",
        "I'm confident with basic Python syntax (variables, loops, functions)",
        "I'm comfortable working with lists and dictionaries",
        'I know how to work with external libraries/packages',
        'I understand object-oriented programming concepts',
        'I can write and debug complex Python programs',
        'None of the above',
      ];
    }

    if (normalizedTopic.includes('spreadsheet')) {
      return [
        "I've interacted with a few spreadsheets before",
        "I'm confident in working with charts in spreadsheets",
        "I'm confident with spreadsheet functions",
        'I know how to create and use pivot tables',
        "I'm a pro with spreadsheet shortcuts",
        'None of the above',
      ];
    }

    if (normalizedTopic.includes('data 101')) {
      return [
        'Explained things in a way that was clear and simple',
        'Spent time helping me individually',
        'Was very good/strict about ensuring I got work done',
        'Their lessons or exercises were engaging',
        'They were incredibly knowledgeable',
        'They were very likeable / funny',
        'Other',
      ];
    }

    if (normalizedTopic.includes('looker')) {
      return [
        "I've interacted with a few Looker visualizations/reports before",
        'I created a few visualizations in Looker Explores before',
        'I know what measures and dimensions are, and how they work',
        'I know how to create table calculations in Looker',
        "I've created my own Dashboards or Looks in Looker before",
        'None of the above',
      ];
    }

    if (normalizedTopic.includes('tableau')) {
      return [
        "I've interacted with a few Tableau visualizations/reports before",
        'I created a few visualizations in Tableau Worksheets before',
        'I know what measures and dimensions are, and how they work',
        "I've connected Tableau Workbooks to my own data",
        "I've created my own Dashboards or Stories in Tableau before",
        'I know how to extend and manipulate the default measures and dimensions in Tableau',
        'None of the above',
      ];
    }

    if (normalizedTopic.includes('sql')) {
      return [
        "I've written a few SQL queries before",
        "I'm confident querying single tables (using SELECT)",
        "I'm confident filtering/sorting tables (using WHERE, ORDER BY)",
        'I know how to join tables (using JOIN)',
        'I know how to combine column values (using MAX, AVG etc)',
        'I know what normalization is',
        'None of the above',
      ];
    }

    // Return null for topics without defined options
    return null;
  };

  // Generate skill knowledge questions for topics that have options
  const skillKnowledgeQuestions: Question[] = learningPlanTopics
    .filter((topic): topic is string => topic != null)
    .map((topic) => {
      const options = getSkillKnowledgeOptions(topic);
      if (!options) return null;

      const displayTopic = topic === 'GenAI' ? 'AI/LLM' : topic;

      return {
        id: `ask-skill-knowledge-${topic.toLowerCase().replace(/\s+/g, '-')}`,
        text: `Choose all (or any!) of the following statements that are true about your current ${displayTopic} knowledge.`,
        type: 'multi-select' as const,
        options: options.map((option) => ({ value: option, label: option })),
      } as MultiSelectQuestion;
    })
    .filter((question): question is MultiSelectQuestion => question !== null);

  const QUESTIONS: Question[] = React.useMemo(
    () => [
      {
        id: 'ask-skill-rating',
        text:
          learningPlanTopics.length === 1
            ? `On a scale of 0-10, how would you rate yourself in ${
                learningPlanTopics[0] === 'GenAI'
                  ? 'AI and LLMs (like ChatGPT)'
                  : learningPlanTopics[0]
              }?`
            : 'On a scale of 0-10, how would you rate yourself in each of the skills below?',
        type: 'skill-rating' as const,
        topics: learningPlanTopics
          .filter((topic) => topic != null)
          .map((topic) =>
            topic === 'GenAI' ? 'AI and LLMs (like ChatGPT)' : topic
          ),
      },
      ...skillKnowledgeQuestions,
      {
        id: 'ask-common-tasks',
        text: `What are common tasks or workflows that you'd like to accomplish more effectively using ${learningPlanTopics
          .slice(0, 2)
          .filter((topic) => topic != null)
          .map((topic) =>
            topic === 'GenAI' || topic === 'Generative AI' ? 'AI / LLMs' : topic
          )
          .join(' or ')}?`,
        type: 'input' as const,
      },
      {
        id: 'ask-notification-time',
        text: "What's your preferred time each weekday to complete the online exercises?",
        subtext:
          "You can work on them at any time, but to help you build a daily habit and allocate time, we'll send a 30min calendar invite at the start of the hour you set below.",
        type: 'timezone-time' as const,
      },
      ...(shouldShowKickOff
        ? [
            {
              id: 'ask-kickoff-time',
              text: 'What is your preferred time for the kick-off call?',
              subtext:
                "You'll get an invite to the kick-off call time that works best for you.",
              type: 'single-select' as const,
              options: kickOffTimes.map((time) => {
                const learnerTimezone = dayjs.tz.guess();
                const timeInLearnerTimezone = dayjs
                  .tz(time, 'UTC')
                  .tz(learnerTimezone);

                return {
                  value: time,
                  label: `${timeInLearnerTimezone.format(
                    'MMMM D'
                  )}, ${timeInLearnerTimezone
                    .format('h:mm')
                    .replace(':00', '')}${timeInLearnerTimezone
                    .format('a')
                    .toLowerCase()}`,
                };
              }),
            },
          ]
        : []),
      {
        id: 'ask-office-hours-time',
        text: 'What times work for you for the 30min weekly mentor office hours?',
        subtext:
          "To help, we've selected all available options - please unselect the ones that do NOT work for you for each of the next 4 weeks.\n\nThe more you keep available, the more likely we'll be able to match you with the best mentor.",
        type: 'multi-select' as const,
        options: officeHoursTimes.map((time) => {
          const learnerTimezone = dayjs.tz.guess();
          const timeInLearnerTimezone = dayjs
            .tz(time, 'UTC')
            .tz(learnerTimezone);
          return {
            value: time,
            label: `${timeInLearnerTimezone.format(
              'dddd'
            )}s ${timeInLearnerTimezone
              .format('h:mm')
              .replace(':00', '')}${timeInLearnerTimezone
              .format('a')
              .toLowerCase()}`,
          };
        }),
      },
      {
        id: 'ask-comments',
        text: "Anything else you'd like to add?",
        subtext:
          'Think of anything else that could be helpful for your mentor to know, including your goals and expectations for the program.',
        type: 'input' as const,
      },
      {
        id: 'loader',
        text: '',
        type: 'loader' as const,
      },
    ],
    [
      kickOffTimes,
      learningPlanTopics,
      officeHoursTimes,
      skillKnowledgeQuestions,
      shouldShowKickOff,
    ]
  );

  const postCohortOnboardingMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      // Create skill knowledge answers object dynamically
      const skillKnowledgeAnswers = skillKnowledgeQuestions.reduce(
        (acc, question) => {
          acc[question.id] =
            (answers?.[question.id] as MultiSelectAnswer) ?? [];
          return acc;
        },
        {} as Record<string, MultiSelectAnswer>
      );

      const parsedAnswers = {
        'ask-notification-time': answers?.['ask-notification-time'] ?? {
          time: '',
          timezone: '',
        },
        'ask-kickoff-time': shouldAnswerKickOff
          ? answers?.['ask-kickoff-time'] ?? ''
          : undefined,
        'ask-office-hours-time': answers?.['ask-office-hours-time'] ?? [],
        'ask-skill-rating': answers?.['ask-skill-rating'] ?? {},
        'ask-comments': (answers?.['ask-comments'] as string) ?? '',
        'ask-common-tasks': (answers?.['ask-common-tasks'] as string) ?? '',
        ...skillKnowledgeAnswers,
      };

      const response = await postCohortOnboarding(
        token,
        parsedAnswers,
        openCohortInvite?.id ?? -1
      );
      return response;
    },
    onError: (error: Error | AxiosError) => {
      toast.error(`Failed to onboard - ${error.message}`);
    },
  });

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Answers>({
    'ask-office-hours-time': officeHoursTimes,
    ...(shouldAnswerKickOff
      ? {
          'ask-kickoff-time': kickOffTimes[0],
        }
      : {}),
  });

  const theme = useTheme();
  const animationParentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!openCohortInvite && !postCohortOnboardingMutation.isLoading) {
      history.push(ROUTES.HOME.getPath());
    }
  }, [openCohortInvite, history, postCohortOnboardingMutation]);

  React.useEffect(() => {
    if (animationParentRef.current) {
      autoAnimate(animationParentRef.current, {
        duration: 300,
        easing: 'ease-in-out',
      });
    }
  }, [animationParentRef]);

  const currentQuestion = QUESTIONS[currentIndex];
  const currentQuestionAnswer = currentQuestion
    ? answers[currentQuestion.id as keyof Answers]
    : undefined;

  // Check if current question is properly answered based on question type
  const isCurrentQuestionAnswered = React.useMemo(() => {
    if (!currentQuestionAnswer && currentQuestion?.type !== 'loader')
      return false;

    switch (currentQuestion?.type) {
      case 'loader':
        return true;
      case 'timezone-time': {
        const timeAnswer = currentQuestionAnswer as NotificationTimeAnswer;
        return !!(timeAnswer?.timezone && timeAnswer?.time);
      }
      case 'single-select':
        return !!(currentQuestionAnswer as SingleSelectAnswer);
      case 'multi-select': {
        const multiAnswer = currentQuestionAnswer as MultiSelectAnswer;
        return Array.isArray(multiAnswer) && multiAnswer.length > 0;
      }
      case 'skill-rating': {
        const skillAnswer = currentQuestionAnswer as SkillRatingAnswer;
        if (!skillAnswer || typeof skillAnswer !== 'object') return false;
        const topics = (currentQuestion as SkillRatingQuestion)?.topics;
        return topics.every(
          (topic) =>
            typeof skillAnswer[topic] === 'number' &&
            skillAnswer[topic] >= 0 &&
            skillAnswer[topic] <= 10
        );
      }
      case 'input':
      case 'learning-plan-input': {
        const inputAnswer = currentQuestionAnswer as InputAnswer;
        return !!(inputAnswer && inputAnswer.trim().length > 0);
      }
      default:
        return false;
    }
  }, [currentQuestionAnswer, currentQuestion]);

  const handleAnswerQuestion = (id: string, answer: Answers[keyof Answers]) => {
    setAnswers((prev) => ({
      ...prev,
      [id]: answer,
    }));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (!postCohortOnboardingMutation.isLoading) {
      postCohortOnboardingMutation.mutate();
    }
  };

  const handleNext = () => {
    if (!isCurrentQuestionAnswered) {
      return;
    }
    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <PageWithAsyncData
      isLoading={isLoadingTeams}
      error={error}
      retry={refreshTeamsData}
    >
      <ScrollContainer ref={animationParentRef}>
        <AnimationContainer key={`animation-${currentQuestion?.id}`}>
          <Column key={`column-${currentQuestion?.id}`}>
            {currentQuestion?.type === 'timezone-time' && (
              <TimezoneTimeQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                answer={
                  currentQuestionAnswer as NotificationTimeAnswer | undefined
                }
                onAnswerQuestion={handleAnswerQuestion}
                onNext={handleNext}
              />
            )}

            {currentQuestion?.type === 'single-select' && (
              <SingleSelectQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                options={currentQuestion?.options ?? []}
                onAnswerQuestion={handleAnswerQuestion}
                answer={currentQuestionAnswer as SingleSelectAnswer}
                onNext={handleNext}
              />
            )}
            {currentQuestion?.type === 'multi-select' && (
              <MultiSelectQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                options={currentQuestion?.options ?? []}
                onAnswerQuestion={handleAnswerQuestion}
                answer={currentQuestionAnswer as MultiSelectAnswer}
              />
            )}
            {currentQuestion?.type === 'skill-rating' && (
              <SkillRatingQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                topics={currentQuestion?.topics}
                onAnswerQuestion={handleAnswerQuestion}
                answer={currentQuestionAnswer as SkillRatingAnswer}
                onNext={handleNext}
              />
            )}
            {currentQuestion?.type === 'learning-plan-input' && (
              <LearningPlanInputQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                answer={currentQuestionAnswer as InputAnswer}
                onAnswerQuestion={handleAnswerQuestion}
                onNext={handleNext}
                learningPlan={currentQuestion?.learningPlan ?? null}
              />
            )}
            {currentQuestion?.type === 'input' && (
              <InputQuestion
                id={currentQuestion?.id}
                key={currentQuestion?.id}
                text={currentQuestion?.text}
                subtext={currentQuestion?.subtext}
                answer={
                  (currentQuestionAnswer as InputAnswer | undefined) ?? ''
                }
                onAnswerQuestion={handleAnswerQuestion}
                onNext={handleNext}
              />
            )}
            {currentQuestion?.type === 'loader' && (
              <LoaderQuestion
                onSubmit={handleSubmit}
                isSubmitting={postCohortOnboardingMutation.isLoading}
                isSubmitComplete={postCohortOnboardingMutation.isSuccess}
                onComplete={async () => {
                  console.log('onComplete');
                  await refreshTeamsData();
                  console.log('refreshTeamsData done');
                }}
              />
            )}
            {currentQuestion?.type !== 'loader' && (
              <NavButton
                label={
                  currentIndex === QUESTIONS.length - 2 ? 'Finish' : 'Next'
                }
                onClick={handleNext}
                isDisabled={
                  postCohortOnboardingMutation.isLoading ||
                  !isCurrentQuestionAnswered
                }
                style={{
                  marginTop: '32px',
                }}
                type="primary"
                faIcon={
                  currentIndex === QUESTIONS.length - 2
                    ? 'fas fa-check'
                    : 'fas fa-arrow-right'
                }
              />
            )}
          </Column>
        </AnimationContainer>
        {currentQuestion?.type !== 'loader' && (
          <NavigationFooter>
            <NavButton
              label="Previous"
              onClick={handlePrevious}
              faIcon="fas fa-arrow-left"
              isDisabled={currentIndex === 0}
              showIconBeforeLabel
              color={theme.colors.textRegular}
              style={{
                border: 'none',
                marginTop: 0,
              }}
            />
            <ProgressWidget
              label="THIS COHORT"
              currentIndex={currentIndex}
              totalQuestions={QUESTIONS.length - 1}
            />
          </NavigationFooter>
        )}
      </ScrollContainer>
    </PageWithAsyncData>
  );
}

const Label = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 400;
  margin-top: 6px;
`;

export function InputQuestion({
  id,
  text,
  subtext,
  answer,
  onAnswerQuestion,
  onNext,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: string;
  onAnswerQuestion: (id: string, answer: string) => void;
  onNext: () => void;
}) {
  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && <P>{subtext}</P>}
      <MultiLineInput
        autoFocus
        value={answer}
        placeholder="Enter your answer here"
        onChange={(e) => onAnswerQuestion(id, e.target.value)}
        shouldSubmitOnEnter
        onSubmit={onNext}
        canSubmit={(answer ?? '').trim().length > 0}
      />
      <Label>Shift+Enter to make a line break</Label>
    </ColumnQuestionWrapper>
  );
}

function parseLearningPlanTopics({
  learningPlan = [],
  modules,
  projects,
}: {
  learningPlan: StaticLearningPlanUnit[];
  modules: TeamsProfilePayload['modules'];
  projects: AppliedProjectStat[];
}) {
  const moduleTopics = (learningPlan ?? [])
    .filter((topic) => topic.type === 'content-module')
    .map((topic) => topic.slug)
    .map((slug) => {
      const module = modules.find((module) => module.content.slug === slug);
      return module?.content?.topic;
    })
    .filter((topic): topic is string => topic != null);

  const projectTopics = (learningPlan ?? [])
    .filter((topic) => topic.type === 'project')
    .map((topic) => topic.slug)
    .map((slug) => {
      const project = projects.find((project) => project.slug === slug);
      return project?.topic;
    })
    .filter((topic): topic is string => topic != null);

  return Array.from(new Set([...moduleTopics, ...projectTopics]));
}

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;
  width: 100%;
  justify-content: flex-start;
`;

const SkillKnowledgeOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
  width: 100%;
  align-items: flex-start;
`;

const Option = styled.div<{ isSelected: boolean }>`
  padding: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  font-size: 16px;
  border-radius: 15px;
  transition: all 0.3s ease-in-out;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.bgActive};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  }

  &:focus-visible {
    outline-offset: 2px;
    ${({ isSelected, theme }) =>
      isSelected
        ? css`
            outline: 1px solid ${theme.colors.primary};
          `
        : css`
            outline: 1px solid ${theme.colors.textRegular};
          `}
  }
`;

const SkillKnowledgeOption = styled(Option)`
  width: fit-content;
  min-width: auto;
`;

export function SingleSelectQuestion({
  id,
  text,
  subtext,
  answer,
  options,
  onAnswerQuestion,
  onNext,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: SingleSelectAnswer;
  options: Array<{
    value: string;
    label: string;
  }>;
  onAnswerQuestion: (id: string, answer: SingleSelectAnswer) => void;
  onNext: () => void;
}) {
  // Get user's timezone for kick off time display
  const learnerTimezone = dayjs.tz.guess();
  const timezoneName = Intl.DateTimeFormat('en', {
    timeZoneName: 'short',
    timeZone: learnerTimezone,
  })
    .format(Date.now())
    .split(' ')[1];

  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && <P>{subtext}</P>}
      {id === 'ask-kickoff-time' && (
        <>
          <P>
            If no times work, choose the one where you'd be most likely to join,
            even if it's for 15min. The kick-off call is really important to
            attend or listen in on, so you know how everything will work.
          </P>
          <ItalicLabel>
            All times shown in your local time ({timezoneName})
          </ItalicLabel>
        </>
      )}
      <OptionsContainer>
        {options.map((option) => (
          <Option
            key={option.value}
            onClick={() => onAnswerQuestion(id, option.value)}
            isSelected={answer === option.value}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const focusedOption = option;

                if (answer && answer === focusedOption.value) {
                  onNext();
                } else {
                  onAnswerQuestion(id, focusedOption.value);
                }
              }
            }}
          >
            {option.label}
          </Option>
        ))}
      </OptionsContainer>
    </ColumnQuestionWrapper>
  );
}

const ItalicLabel = styled(PSmall)`
  font-style: italic;
`;

export function MultiSelectQuestion({
  id,
  text,
  subtext,
  answer,
  options,
  onAnswerQuestion,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: MultiSelectAnswer;
  options: Array<{
    value: string;
    label: string;
  }>;
  onAnswerQuestion: (id: string, answer: MultiSelectAnswer) => void;
}) {
  const currentAnswer = answer || [];
  const isSkillKnowledgeQuestion = id.startsWith('ask-skill-knowledge-');

  const selectOption = (option: string) => {
    const isSelected = currentAnswer.includes(option);
    const isNoneOfTheAbove = option === 'None of the above';

    if (isNoneOfTheAbove) {
      // If "None of the above" is clicked, clear all other selections and only select this
      if (isSelected) {
        onAnswerQuestion(id, []);
      } else {
        onAnswerQuestion(id, [option]);
      }
    } else {
      // If any other option is clicked, remove "None of the above" if it was selected
      let newAnswer: string[];

      if (isSelected) {
        newAnswer = currentAnswer.filter((a) => a !== option);
      } else {
        newAnswer = [
          ...currentAnswer.filter((a) => a !== 'None of the above'),
          option,
        ];
      }

      onAnswerQuestion(id, newAnswer);
    }
  };

  const OptionsContainerComponent = isSkillKnowledgeQuestion
    ? SkillKnowledgeOptionsContainer
    : OptionsContainer;
  const OptionComponent = isSkillKnowledgeQuestion
    ? SkillKnowledgeOption
    : Option;

  // Get user's timezone for office hours display
  const learnerTimezone = dayjs.tz.guess();
  const timezoneName = Intl.DateTimeFormat('en', {
    timeZoneName: 'short',
    timeZone: learnerTimezone,
  })
    .format(Date.now())
    .split(' ')[1];

  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && id === 'ask-office-hours-time' ? (
        <>
          <P>
            To help, we've selected all available options -{' '}
            <strong>
              please unselect the ones that do NOT work for you for each of the
              next 4 weeks.
            </strong>
            <br />
            <br />
            The more you keep available, the more likely we'll be able to match
            you with the best mentor.
          </P>
          <ItalicLabel>
            All times below in your local time ({timezoneName})
          </ItalicLabel>
        </>
      ) : (
        subtext && <P>{subtext}</P>
      )}
      <OptionsContainerComponent>
        {options.map((option) => (
          <OptionComponent
            key={option.value}
            onClick={() => selectOption(option.value)}
            isSelected={currentAnswer.includes(option.value)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const focusedOption = option.value;
                selectOption(focusedOption);
              }
            }}
          >
            {option.label}
          </OptionComponent>
        ))}
      </OptionsContainerComponent>
    </ColumnQuestionWrapper>
  );
}

function LearningPlanInputQuestion({
  id,
  text,
  subtext,
  answer,
  onAnswerQuestion,
  onNext,
  learningPlan,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: string;
  onAnswerQuestion: (id: string, answer: string) => void;
  onNext: () => void;
  learningPlan: StaticLearningPlanUnit[] | null;
}) {
  return (
    <ColumnQuestionWrapper>
      <H3 style={{ marginTop: 108 }}>
        Your personalized learning plan is ready!
      </H3>
      {subtext && <P>{subtext}</P>}
      <LearningPlanWrapper style={{ marginTop: 46 }}>
        {(learningPlan ?? []).map((unit, ind) => (
          <LearningPlanUnitCard
            key={unit.slug}
            learningUnit={mapLearningPlanUnit(unit)}
            index={ind + 1}
            numItems={learningPlan?.length ?? 0}
            isPreviewingPlan
          />
        ))}
      </LearningPlanWrapper>
      <P style={{ marginTop: 32 }}>{text}</P>

      <MultiLineInput
        // autoFocus
        value={answer}
        placeholder="Enter your feedback here"
        onChange={(e) => onAnswerQuestion(id, e.target.value)}
        shouldSubmitOnEnter
        onSubmit={onNext}
        canSubmit={(answer ?? '').trim().length > 0}
      />
    </ColumnQuestionWrapper>
  );
}

const RatingHeader = styled(P)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 16px;
`;

export function SkillRatingQuestion({
  id,
  text,
  subtext,
  topics,
  onAnswerQuestion,
  answer,
}: {
  id: string;
  text: string;
  subtext?: string;
  topics: string[];
  onAnswerQuestion: (id: string, answer: SkillRatingAnswer) => void;
  answer: SkillRatingAnswer;
  onNext: () => void;
}) {
  const currentAnswer = answer || {};

  const handleRating = (topic: string, rating: number) => {
    const newAnswer = {
      ...currentAnswer,
      [topic]: rating,
    };
    onAnswerQuestion(id, newAnswer);
  };

  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && <P>{subtext}</P>}

      {topics.length > 1 && (
        <div style={{ marginTop: 32 }}>
          {topics.map((topic) => (
            <TopicRatingSection key={topic}>
              <RatingHeader>{topic}</RatingHeader>
              <RatingButtonsContainer>
                {Array.from({ length: 11 }, (_, i) => (
                  <RatingButton
                    key={i}
                    onClick={() => handleRating(topic, i)}
                    isSelected={currentAnswer[topic] === i}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRating(topic, i);
                      }
                    }}
                  >
                    {i}
                  </RatingButton>
                ))}
              </RatingButtonsContainer>
              <RatingLabelsContainer>
                <RatingLabel>Limited experience</RatingLabel>
                <RatingLabel>Casual user</RatingLabel>
                <RatingLabel>Regular user</RatingLabel>
                <RatingLabel>Power user or expert</RatingLabel>
              </RatingLabelsContainer>
            </TopicRatingSection>
          ))}
        </div>
      )}

      {topics.length === 1 && (
        <div style={{ marginTop: 32 }}>
          <RatingButtonsContainer>
            {Array.from({ length: 11 }, (_, i) => (
              <RatingButton
                key={i}
                onClick={() => handleRating(topics[0], i)}
                isSelected={currentAnswer[topics[0]] === i}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRating(topics[0], i);
                  }
                }}
              >
                {i}
              </RatingButton>
            ))}
          </RatingButtonsContainer>
          <RatingLabelsContainer>
            <RatingLabel>Limited experience</RatingLabel>
            <RatingLabel>Casual user</RatingLabel>
            <RatingLabel>Regular user</RatingLabel>
            <RatingLabel>Power user or expert</RatingLabel>
          </RatingLabelsContainer>
        </div>
      )}
    </ColumnQuestionWrapper>
  );
}

const TopicRatingSection = styled.div`
  margin-bottom: 40px;
`;

const RatingButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const RatingButton = styled.div<{ isSelected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  border: 2px solid ${({ theme }) => theme.colors.bgActive};
  /* background-color: ${({ theme }) => theme.colors.bgDefaultSecondary}; */
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.3s ease-in-out;
  padding-bottom: 0;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.buttonPrimary};
      background-color: ${theme.colors.buttonPrimary};
      color: ${theme.colors.buttonTextColor};
    `}

  &:hover {
    border-color: ${({ theme }) => theme.colors.buttonPrimary};
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.buttonPrimary : theme.colors.bgActive};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.buttonPrimary};
    outline-offset: 2px;
  }
`;

const RatingLabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const RatingLabel = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  font-weight: 400;
  margin-top: 8px;
`;

function LoaderQuestion({
  onSubmit,
  isSubmitting,
  isSubmitComplete,
  onComplete,
}: {
  onSubmit: () => void;
  isSubmitting: boolean;
  isSubmitComplete: boolean;
  onComplete: () => void;
}) {
  const [showTitle, setShowTitle] = React.useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = React.useState(false);

  // Start submitting immediately when component mounts
  React.useEffect(() => {
    if (!isSubmitting && !isSubmitComplete) {
      onSubmit();
    }
  }, [onSubmit, isSubmitting, isSubmitComplete]);

  // Redirect when both animation and API call are complete
  React.useEffect(() => {
    if (isAnimationComplete && isSubmitComplete) {
      onComplete();
    }
  }, [isAnimationComplete, isSubmitComplete, onComplete]);

  const { RiveComponent } = useRive({
    src: loaderAnim,
    autoplay: true,
    onPlay: () => {
      setShowTitle(true);
    },
    onStop: () => {
      setIsAnimationComplete(true);
    },
  });

  return (
    <ColumnQuestionWrapper>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
        }}
      >
        <RiveComponent style={{ width: '286px', height: '92px' }} />
        {showTitle && (
          <P style={{ opacity: 0.5, marginTop: '16px' }}>
            Creating your personalized learning experience...
          </P>
        )}
      </div>
    </ColumnQuestionWrapper>
  );
}
